import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { cn } from "../../../lib/utils.js";
import { QuizBlank } from "../shared/quiz-blank.js";
import { NumberKeypad } from "../shared/number-keypad.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import { useFillBlankQuiz } from "../hooks/use-fill-blank-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function FillBlankQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  useKeypad = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  var _a;
  const { t } = useTranslation("quiz");
  const {
    answers,
    currentIndex,
    blankCount,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleBlankClick,
    handleInputChange,
    handleKeypadInput,
    handleKeypadClear,
    handleSubmit
  } = useFillBlankQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const renderedProblem = useMemo(() => {
    if (blankCount === 0) return [];
    return formatProblem(
      quiz.problem,
      `${quizId}-problem`,
      (index, content) => {
        var _a2, _b;
        const key = content || "";
        return /* @__PURE__ */ jsx(
          QuizBlank,
          {
            index,
            answers,
            currentIndex,
            isCorrect: isSolved ? (_a2 = solveResult == null ? void 0 : solveResult.corrects) == null ? void 0 : _a2[index] : null,
            isSolved,
            showCorrectAnswer: shouldShowCorrectAnswers,
            correctAnswer: (_b = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _b[index],
            onClick: () => handleBlankClick(index),
            onChange: (value) => handleInputChange(index, value, key),
            useKeypad,
            isViewSolved
          },
          `blank-${quizId}-${index}`
        );
      },
      quiz.additionalImages ?? void 0
    );
  }, [blankCount, currentIndex, isSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, useKeypad, isViewSolved, handleBlankClick, handleInputChange, answers, quiz.additionalImages]);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-6", useKeypad && !isSolved && "pb-48"), children: [
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
            /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-blue-500 rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.fillBlank.label") }) }),
            /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
            /* @__PURE__ */ jsx(DebugQuizId, { quizId })
          ] }),
          /* @__PURE__ */ jsx("h3", { className: "text-2xl font-bold leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        className: cn(
          "p-4 rounded-xl bg-gray-100",
          "text-xl text-gray-700",
          "[&_.formatProblem]:leading-relaxed",
          '[&_span[class*="inline-flex"]]:leading-normal'
        ),
        children: renderedProblem
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers ? (_a = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _a.join(", ") : void 0,
        disableSound
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    ),
    useKeypad && !isSolved && !isViewSolved && /* @__PURE__ */ jsx(
      NumberKeypad,
      {
        onInput: handleKeypadInput,
        onClear: handleKeypadClear,
        disabled: isSolved
      }
    )
  ] });
}
export {
  FillBlankQuizAdult
};
//# sourceMappingURL=fill-blank-adult.js.map
